/**
 * AssemblyTreeViewer - 12-25 Tariff Analysis
 *
 * Displays the reconstructed BOM tree for user review and approval.
 * Part of the PENDING_APPROVAL workflow.
 *
 * Features:
 * - Expandable/collapsible tree nodes
 * - Color coding: Matched (green), Missing (red)
 * - Part number, description, quantity for each item
 * - Hover tooltip explaining how each item was matched
 * - Approve button to trigger final assembly classification
 */

import { useState } from 'react';
import { FiChevronRight, FiChevronDown, FiCheck, FiAlertCircle, FiBox, FiInfo } from 'react-icons/fi';
import { IoGitBranch } from 'react-icons/io5';

// Helper to get match method icon and color
function getMatchMethodStyle(matchInfo) {
  if (!matchInfo) return { icon: 'folder', color: 'text-gray-500', bgColor: 'bg-gray-100' };

  const method = matchInfo.method || matchInfo.source || '';

  switch (method) {
    case 'folder_structure':
      return { icon: 'folder', color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Folder' };
    case 'normalized_part_number':
    case 'bom_lookup':
      return { icon: 'link', color: 'text-purple-600', bgColor: 'bg-purple-50', label: 'BOM Lookup' };
    case 'sibling_in_folder':
    case 'sibling':
      return { icon: 'sibling', color: 'text-cyan-600', bgColor: 'bg-cyan-50', label: 'Sibling' };
    case 'bom_extraction':
    case 'bom_missing':
      return { icon: 'missing', color: 'text-red-600', bgColor: 'bg-red-50', label: 'BOM (Missing)' };
    case 'orphan':
    case 'standalone':
      return { icon: 'orphan', color: 'text-amber-600', bgColor: 'bg-amber-50', label: 'Standalone' };
    default:
      return { icon: 'unknown', color: 'text-gray-500', bgColor: 'bg-gray-100', label: 'Unknown' };
  }
}

// Recursive tree node component
function TreeNode({ node, depth = 0 }) {
  const [isExpanded, setIsExpanded] = useState(depth < 2); // Auto-expand first 2 levels
  const [showTooltip, setShowTooltip] = useState(false);

  const hasChildren = node.children && node.children.length > 0;
  const isMatched = node.status === 'matched';
  const isMissing = node.status === 'missing';
  const matchInfo = node.match_info;
  const matchStyle = getMatchMethodStyle(matchInfo);

  // Status indicator colors
  const statusColor = isMatched
    ? 'text-green-600 bg-green-50 border-green-200'
    : isMissing
      ? 'text-red-600 bg-red-50 border-red-200'
      : 'text-gray-600 bg-gray-50 border-gray-200';

  const statusIcon = isMatched ? (
    <FiCheck className="w-4 h-4" />
  ) : isMissing ? (
    <FiAlertCircle className="w-4 h-4" />
  ) : (
    <FiBox className="w-4 h-4" />
  );

  return (
    <div className="select-none">
      {/* Node row */}
      <div
        className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50
                    cursor-pointer transition-colors ${depth === 0 ? 'bg-green-50' : ''}`}
        style={{ marginLeft: `${depth * 24}px` }}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600">
            {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
          </button>
        ) : (
          <div className="w-5 h-5" /> // Spacer for alignment
        )}

        {/* Status indicator */}
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full
                         border ${statusColor}`}>
          {statusIcon}
        </span>

        {/* Part info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono font-medium text-gray-900 truncate">
              {node.part_number || 'Unknown'}
            </span>
            {node.qty > 1 && (
              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                x{node.qty}
              </span>
            )}
          </div>
          {node.description && (
            <div className="text-sm text-gray-500 truncate">
              {node.description}
            </div>
          )}
        </div>

        {/* Match method badge with tooltip */}
        {matchInfo && (
          <div className="relative">
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full cursor-help flex items-center gap-1 ${matchStyle.bgColor} ${matchStyle.color}`}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <FiInfo className="w-3 h-3" />
              {matchStyle.label}
            </span>

            {/* Tooltip */}
            {showTooltip && (
              <div className="absolute z-50 right-0 top-full mt-1 w-80 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl"
                   style={{ maxWidth: '320px' }}>
                <div className="font-semibold mb-2 text-gray-100">How this item was matched:</div>
                <div className="text-gray-300 leading-relaxed">
                  {matchInfo.explanation || 'No match information available'}
                </div>
                {matchInfo.normalized_key && (
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <span className="text-gray-400">Normalized key: </span>
                    <span className="font-mono text-green-400">{matchInfo.normalized_key}</span>
                  </div>
                )}
                {matchInfo.matched_filename && (
                  <div className="mt-1">
                    <span className="text-gray-400">Matched file: </span>
                    <span className="font-mono text-blue-400">{matchInfo.matched_filename}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Status badge (if no match info) */}
        {!matchInfo && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor}`}>
            {isMatched ? 'Matched' : isMissing ? 'Missing' : node.status || 'Unknown'}
          </span>
        )}
      </div>

      {/* Children (recursive) */}
      {hasChildren && isExpanded && (
        <div className="border-l-2 border-gray-200 ml-6">
          {node.children.map((child, idx) => (
            <TreeNode key={`${child.part_number}-${idx}`} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AssemblyTreeViewer({
  assemblyTree,
  onApprove,
  isApproving = false,
  fileIds = []
}) {
  if (!assemblyTree) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="text-center text-gray-500">
          <IoGitBranch className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No assembly tree available</p>
        </div>
      </div>
    );
  }

  const { root, matched_count = 0, missing_count = 0, doc_count = 0 } = assemblyTree;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-green-900 to-green-800">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <IoGitBranch className="w-6 h-6" />
          Assembly Structure Review
        </h3>
        <p className="text-green-100 text-sm mt-1">
          Review the reconstructed BOM tree before classification
        </p>
      </div>

      {/* Summary stats */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-wrap gap-4 mb-3">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{matched_count}</span> Matched
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            <span className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{missing_count}</span> Missing
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-gray-400"></span>
            <span className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{doc_count}</span> Documents
            </span>
          </div>
        </div>
        {/* Match method legend */}
        <div className="flex flex-wrap gap-2 text-xs pt-2 border-t border-gray-200">
          <span className="text-gray-500">Match types:</span>
          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600" title="Matched via folder structure in uploaded ZIP">Folder</span>
          <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-600" title="Matched via BOM table part number lookup">BOM Lookup</span>
          <span className="px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-600" title="File in same folder as parent assembly">Sibling</span>
          <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-600" title="Document not referenced in any BOM">Standalone</span>
          <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-600" title="Listed in BOM but no matching document found">Missing</span>
          <span className="text-gray-400 ml-1 flex items-center gap-1">
            <FiInfo className="w-3 h-3" /> Hover badges for details
          </span>
        </div>
      </div>

      {/* Tree view */}
      <div className="p-4 max-h-96 overflow-y-auto">
        {root ? (
          <TreeNode node={root} depth={0} />
        ) : (
          <div className="text-center text-gray-500 py-8">
            <p>No root assembly found</p>
            <p className="text-sm">Upload drawings with BOM tables to see the assembly structure</p>
          </div>
        )}
      </div>

      {/* Missing parts warning */}
      {missing_count > 0 && (
        <div className="mx-4 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <FiAlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {missing_count} BOM item(s) could not be matched to uploaded drawings
              </p>
              <p className="text-xs text-amber-600 mt-1">
                The assembly classification will proceed with available components.
                Missing items may affect the final HTS code determination.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Approve button */}
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <button
          onClick={() => onApprove && onApprove(fileIds, assemblyTree)}
          disabled={isApproving || !root}
          className={`w-full py-3 px-6 rounded-lg font-semibold transition-all
                     flex items-center justify-center gap-2
                     ${isApproving || !root
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-700 text-white hover:bg-green-800 active:bg-green-900'
            }`}
        >
          {isApproving ? (
            <>
              <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Approving...
            </>
          ) : (
            <>
              <FiCheck className="w-5 h-5" />
              Approve & Classify Assembly
            </>
          )}
        </button>
      </div>
    </div>
  );
}
